// HYK-1115: shared embedded Stripe Connect onboarding logic, previously
// duplicated between CompleteProfileV2 (registration) and AddCreation (publish).
import { useCallback, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { getAccountSession, getKycStatus, refreshCapabilities } from '../Reducers/actions/accountActions';

// `has_light_kyc` is intentionally passed through raw (not coerced to a
// boolean here): CompleteProfileV2 treats a missing value as "not verified"
// (`=== true`) while AddCreation treats it as "still verified until proven
// otherwise" (`!== false`) at its other KYC call sites — each caller applies
// its own existing convention when reading the field.
export const normalizeKycStatus = (data) => ({
  has_light_kyc: data?.has_light_kyc,
  has_full_kyc: data?.has_full_kyc === true,
  currently_due: Array.isArray(data?.currently_due) ? data.currently_due : [],
  eventually_due: Array.isArray(data?.eventually_due) ? data.eventually_due : [],
  account_id: data?.account_id || null,
});

/**
 * Owns the Stripe Connect JS instance behind the embedded onboarding
 * component (`ConnectAccountOnboarding`). Callers stay responsible for
 * resolving/creating the Stripe account id and for deriving their own
 * gating state from the normalized KYC status this returns on exit.
 */
export const useStripeConnectEmbeddedOnboarding = () => {
  const [connectInstance, setConnectInstance] = useState(null);

  const initializeEmbeddedOnboarding = useCallback(async (accountId) => {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      throw new Error('Stripe publishable key is not configured');
    }

    const instance = loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret: async () => {
        const sessionResponse = await getAccountSession(accountId);
        if (!sessionResponse?.success || !sessionResponse?.data?.client_secret) {
          throw new Error('Unable to initialize Stripe onboarding session');
        }
        return sessionResponse.data.client_secret;
      },
    });

    setConnectInstance(instance);
    return instance;
  }, []);

  // Fires on both successful completion and abandon/close — Stripe's
  // `onExit` doesn't distinguish the two. Callers derive "still blocked" vs.
  // "done" from the returned status. Always clears the instance so the
  // embedded UI unmounts either way; errors propagate for caller-specific messaging.
  const handleEmbeddedOnboardingExit = useCallback(async () => {
    try {
      await refreshCapabilities();
      const response = await getKycStatus();
      if (!response?.success || !response?.data) {
        throw new Error(response?.message || 'Unable to refresh verification status right now.');
      }
      return normalizeKycStatus(response.data);
    } finally {
      setConnectInstance(null);
    }
  }, []);

  return {
    connectInstance,
    initializeEmbeddedOnboarding,
    handleEmbeddedOnboardingExit,
  };
};
